import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    CircularProgress,
    Alert,
    Avatar,
    Typography
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { getAISupportConfiguration, updateAISupportConfiguration } from '../Api';
import AISupportConfigurationDto from '../models/dtos/AISupportConfigurationDto';
import UpdateAISupportConfigurationDto from '../models/dtos/UpdateAISupportConfigurationDto';

interface AISupportConfigModalProps {
    open: boolean;
    onClose: () => void;
    onSave?: () => void;
}

const AISupportConfigModal: React.FC<AISupportConfigModalProps> = ({ open, onClose, onSave }) => {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    
    const [config, setConfig] = useState<AISupportConfigurationDto | null>(null);
    
    // Form state
    const [configId, setConfigId] = useState<number | null>(null);
    const [responseTimeout, setResponseTimeout] = useState<number>(30);
    const [chatAutoCloseTimeoutInHours, setChatAutoCloseTimeoutInHours] = useState<number>(72);
    const [maxRetrials, setMaxRetrials] = useState<number>(3);
    const [supportUserName, setSupportUserName] = useState('');
    const [supportUserAvatarUrl, setSupportUserAvatarUrl] = useState('');

    useEffect(() => {
        if (open) {
            loadConfiguration();
        }
    }, [open]);

    const loadConfiguration = async () => {
        setLoading(true);
        setError(null);
        try {
            const configData = await getAISupportConfiguration();

            if (configData) {
                setConfig(configData);
                setConfigId(configData.id);
                setResponseTimeout(configData.responseTimeout || 30);
                setChatAutoCloseTimeoutInHours(configData.chatAutoCloseTimeoutInHours || 72);
                setMaxRetrials(configData.maxRetrials || 3);
                setSupportUserName(configData.supportUserName || '');
                setSupportUserAvatarUrl(configData.supportUserAvatarUrl || '');
            }
        } catch (err) {
            setError('Failed to load configuration');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        setSuccessMessage(null);

        try {
            const updateData: UpdateAISupportConfigurationDto = {
                responseTimeout,
                chatAutoCloseTimeoutInHours,
                maxRetrials,
                supportUserName,
                supportUserAvatarUrl
            };

            const result = await updateAISupportConfiguration(updateData);

            if (result) {
                setSuccessMessage('Configuration updated successfully');
                setTimeout(() => {
                    if (onSave) onSave();
                    onClose();
                }, 1500);
            } else {
                setError('Failed to update configuration');
            }
        } catch (err) {
            setError('An error occurred while saving');
            console.error(err);
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        setError(null);
        setSuccessMessage(null);
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth>
            <DialogTitle>AI Support Configuration</DialogTitle>
            <DialogContent>
                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                        {error && <Alert severity="error">{error}</Alert>}
                        {successMessage && <Alert severity="success">{successMessage}</Alert>}

                        {configId !== null && (
                            <TextField
                                label="Configuration ID"
                                value={configId}
                                fullWidth
                                disabled
                                helperText="Read-only configuration identifier"
                            />
                        )}

                        <Alert severity="info">
                            The prompts and the model of Betty and of the support tasks (reply rewrite, chat summary) are
                            managed in the AI hub: agent{' '}
                            <RouterLink to="/ai/agents/support.betty">support.betty</RouterLink>, tasks{' '}
                            <RouterLink to="/ai/tasks?ownerService=betup-server-messaging">of betup-server-messaging</RouterLink>.
                            Usage, cost and budgets are in AI › Usage and AI › Budgets. The settings below are the response timeout, chat auto-close, retries and the support user.
                        </Alert>

                        <TextField
                            label="Response Timeout (seconds)"
                            type="number"
                            value={responseTimeout}
                            onChange={(e) => setResponseTimeout(parseInt(e.target.value) || 0)}
                            fullWidth
                            required
                            inputProps={{ min: 1 }}
                        />

                        <TextField
                            label="Chat Auto-Close Timeout (hours)"
                            type="number"
                            value={chatAutoCloseTimeoutInHours}
                            onChange={(e) => setChatAutoCloseTimeoutInHours(parseInt(e.target.value) || 1)}
                            fullWidth
                            required
                            inputProps={{ min: 1, max: 720 }}
                            helperText="Inactive chats will be automatically closed after this many hours (1-720 hours / 30 days)"
                        />

                        <TextField
                            label="Max Retrials"
                            type="number"
                            value={maxRetrials}
                            onChange={(e) => setMaxRetrials(parseInt(e.target.value) || 0)}
                            fullWidth
                            required
                            inputProps={{ min: 0, max: 10 }}
                            helperText="Maximum number of retry attempts for failed AI requests (0-10)"
                        />

                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
                                <TextField
                                    label="Support User Name"
                                    value={supportUserName}
                                    onChange={(e) => setSupportUserName(e.target.value)}
                                    fullWidth
                                    required
                                />

                                <TextField
                                    label="Support User Avatar URL"
                                    value={supportUserAvatarUrl}
                                    onChange={(e) => setSupportUserAvatarUrl(e.target.value)}
                                    fullWidth
                                    placeholder="https://..."
                                />
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, minWidth: '100px' }}>
                                <Typography variant="caption" color="textSecondary">
                                    Preview
                                </Typography>
                                <Avatar
                                    src={supportUserAvatarUrl}
                                    alt={supportUserName || 'Support User'}
                                    sx={{ width: 80, height: 80 }}
                                    imgProps={{
                                        onError: (e: any) => {
                                            e.target.style.display = 'none';
                                        }
                                    }}
                                />
                            </Box>
                        </Box>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCancel} disabled={saving}>
                    Cancel
                </Button>
                <Button 
                    onClick={handleSave} 
                    variant="contained" 
                    color="primary"
                    disabled={loading || saving || !supportUserName}
                >
                    {saving ? <CircularProgress size={24} /> : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AISupportConfigModal;
